import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { ReactComponent as LirariLogo } from './../../assets/images/lirari-logo-small.svg'
import LogoutButton from '../logout'
import { useAuth0 } from '@auth0/auth0-react'
import { useAtom } from 'jotai'
import userClient from '../../Api/Lirari/Clients/userClient'
import { myUserAtom } from '../../State/Musician/myUserAtom'
import { genresAtom } from '../../State/Musician/genresAtom'
import genreClient from '../../Api/Lirari/Clients/genreClient'
import MobileBurgerMenu from './mobileBurgerMenu'
import offeringClient from '../../Api/Lirari/Clients/offeringClient'
import { offeringsAtom } from '../../State/Musician/offeringsAtom'
import { isEmailBeingVerifiedAtom } from '../../State/Musician/isEmailBeingVerifiedAtom'

const NavBar = () => {
  const navigate = useNavigate()
  const path = useLocation().pathname
  const { isAuthenticated, getAccessTokenSilently, user } = useAuth0()
  const [myUser, setMyUser] = useAtom(myUserAtom)
  const [isEmailBeingVerified] = useAtom(isEmailBeingVerifiedAtom)

  const [, setGenres] = useAtom(genresAtom)
  const [, setOfferings] = useAtom(offeringsAtom)

  React.useEffect(() => {
    ;(async () => {
      if (isAuthenticated && user?.sub) {
        // Check if email is verified
        if (!user.email_verified && !isEmailBeingVerified) {
          navigate('/verify-email')
          return
        }

        const token = await getAccessTokenSilently({
          authorizationParams: {
            scope: 'user:all',
          },
        })

        const lirariUser = await userClient.getUserByAuth0Id(user.sub, token)
        if (!lirariUser) {
          return
        }

        setMyUser({
          id: lirariUser.id,
          auth0Id: lirariUser.auth0Id,
          firstName: lirariUser.firstName,
          lastName: lirariUser.lastName,
          email: lirariUser.email,
          about: lirariUser.about,
          locationFormattedString: lirariUser.locationFormattedString,
          socialLinks: {
            youtube: lirariUser.youTubeUrl,
            spotify: lirariUser.spotifyUrl,
            soundCloud: lirariUser.soundCloudUrl,
          },
          userSkills: lirariUser.userSkills ?? [],
          userGenres: lirariUser.userGenres ?? [],
          userOfferings: lirariUser.userOfferings ?? [],
          profileImageUrl:
            lirariUser.profileImageUrl ||
            'https://storage.googleapis.com/demo-profile/images/the-musician-unblurred.png',
          portfolioLinks: lirariUser.portfolioLinks ?? [],
          profileAudioUrl:
            lirariUser.profileAudioUrl || 'https://storage.googleapis.com/demo-profile/audio/Wartime%204.3.m4a',
        })
      }
    })()
  }, [
    isAuthenticated,
    user?.sub,
    getAccessTokenSilently,
    setMyUser,
    navigate,
    user?.email_verified,
    isEmailBeingVerified,
  ])

  React.useEffect(() => {
    ;(async () => {
      const genres = await genreClient.getGenres()
      setGenres(genres)
    })()
  }, [setGenres])

  React.useEffect(() => {
    ;(async () => {
      const offerings = await offeringClient.getOfferings()
      setOfferings(offerings)
    })()
  }, [setOfferings])

  return (
    <div className={`relative flex w-full justify-between h-[5%] items-center bg-[#242424] text-white z-10`}>
      <div className="flex cursor-pointer items-center" onClick={() => navigate('/')}>
        <LirariLogo className="text-white h-[80%] w-[30%] pl-3 pr-2 py-1" />
        <p className="lg:ml-2 text-2xl">Lirari</p>
      </div>

      <div className="space-x-5 py-1 mr-4 lg:mr-8 hidden lg:flex">
        {/* If we are on example profile don't show example profile navigation button, otherwise show */}
        {path !== '/example-profile' ? (
          <a href={`/example-profile`} className="text-white">
            Example Profile
          </a>
        ) : (
          <></>
        )}
        {/* If we are authenticated and not on our user page, show my profile button */}
        {isAuthenticated && path !== `/u/${myUser.id}` ? (
          <a href={`/u/${myUser.id}`} className="text-white">
            My Profile
          </a>
        ) : (
          <></>
        )}
        {/* If we are authenticated and not on search page, show search page */}
        {isAuthenticated && path !== `/search` ? (
          <a href={`/search`} className="text-white">
            Search
          </a>
        ) : (
          <></>
        )}
        {/* If we are not authenticated show sign in button, otherwise show log out button */}
        {!isAuthenticated ? (
          <a href={'/login'} className="text-white">
            Sign In
          </a>
        ) : (
          <LogoutButton />
        )}
      </div>
      <MobileBurgerMenu />
    </div>
  )
}

export default NavBar
